"use client";

import React, { useRef } from "react";
import { ButtonProps } from "../types";

const Button: React.FC<ButtonProps> = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  onClick,
  disabled = false,
  href,
}) => {
  const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);
  const isHoveredRef = useRef<boolean>(false);

  const handleMouseEnter = () => {
    if (!buttonRef.current) return;
    isHoveredRef.current = true;
  };

  const handleMouseLeave = () => {
    if (!buttonRef.current) return;
    isHoveredRef.current = false;
  };

  const getVariantClasses = () => {
    switch (variant) {
      case "primary":
        return "text-omto-blue shadow-blue btn-animated";
      case "secondary":
        return "bg-omto-blue text-white btn-skew-static";
      case "contact":
        return "bg-omto-blue text-white shadow-lg shadow-purple-400/60 btn-contact hover:shadow-purple-400/80";
      default:
        return "bg-omto-blue text-white";
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "btn-sm";
      case "md":
        return "btn-md";
      case "lg":
        return "btn-lg";
      default:
        return "btn-md";
    }
  };

  const getContentClass = () => {
    return "btn-skewed-content";
  };

  const buttonClasses = `
    btn-base btn-focus inline-block
    ${getVariantClasses()}
    ${getSizeClasses()}
    ${className}
    ${disabled ? "btn-disabled" : ""}
  `;

  const content = <span className={getContentClass()}>{children}</span>;

  if (href) {
    return (
      <a
        ref={buttonRef as React.RefObject<HTMLAnchorElement>}
        href={href}
        className={buttonClasses}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={buttonRef as React.RefObject<HTMLButtonElement>}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {content}
    </button>
  );
};

export default Button;
